/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-key */
// Components==============
import Checkbox from 'components/UI/Checkbox/Checkbox';
import EvidencePreview from 'components/UI/EvidencePreview/EvidencePreview';
import { AnimatePresence, motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { AssessmentContext } from 'pages/Feedback/Feedback';
import React, { useContext, useState } from 'react';
import { framerFade } from 'utilities/FramerAnimations';
import { getActivityBySpark } from 'utilities/getActivityBySpark';
import { getFeedbackByUser } from 'utilities/getFeedbackByUser';
import { AssessmentItemContext } from '../../FeedbackItem';
import ReviewTextArea from '../../ReflectionTextarea/ReviewTextArea';
import ReviewItem from '../ReviewItem/ReviewItem';
import './FeedbackInput.module.scss';
import ChevronArrow from './components/ChevronArrow';
// =========================

const ActivityItem = ({ spark }: { spark: SparkEntity }) => {
  const { assessment, activities, trainings } = useContext(AssessmentContext);

  const activityBySpark = getActivityBySpark(
    spark,
    activities,
    trainings,
    assessment
  );

  return (
    <div styleName="activities">
      {activityBySpark.length > 0 && (
        <>
          <p>Behaald tijdens:</p>
          {activityBySpark.length === 1 ? (
            <>
              {activityBySpark[0]?.type &&
              activityBySpark[0]?.type === 'training' ? (
                <span>"Training: {activityBySpark[0].name}"</span>
              ) : (
                <span>"{activityBySpark[0].name}"</span>
              )}
            </>
          ) : (
            <>
              {activityBySpark.slice(0, -1).map((activity, index) => (
                <React.Fragment key={activity.name}>
                  {index > 0 && <span>, </span>}
                  <>
                    {activity?.type && activity?.type === 'training' ? (
                      <span>"Training: {activity.name}"</span>
                    ) : (
                      <span>"{activity.name}"</span>
                    )}
                  </>
                </React.Fragment>
              ))}
              <p>en</p>
              <>
                {activityBySpark[activityBySpark.length - 1]?.type &&
                activityBySpark[activityBySpark.length - 1]?.type ===
                  'training' ? (
                  <span>
                    "Training:{' '}
                    {activityBySpark[activityBySpark.length - 1]?.name}"
                  </span>
                ) : (
                  <span>
                    "{activityBySpark[activityBySpark.length - 1]?.name}"
                  </span>
                )}
              </>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default function FeedbackInput({
  sparks,
  skillId,
}: {
  sparks: SparkEntity[];
  skillId: number;
}) {
  const { assessment, activities, trainings } = useContext(AssessmentContext);

  const {
    firstPositiveValue,
    setFirstPositiveValue,
    firstImprovementValue,
    setFirstImprovementValue,
    secondValue,
    setSecondValue,
    firstReviewer,
    firstImprovementReviewer,
    secondReviewer,
    isChecked,
    secondAssessorAgrees,
    setIsChecked,
  } = useContext(AssessmentItemContext);

  const [showSparks, setShowSparks] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const projectId = assessment?.project_id || 0;
  const userId = useRedux((state) => state.authentication?.user?.id);

  const project = useRedux((state) => state.project?.entities.byId[projectId]);

  const isLocked = !!project?.locked_for_assessment;

  const skill = assessment?.skills.find((ski) => ski.id === skillId);
  const projectEvidence = skill?.evidence || [];

  const projectFeedback =
    skill?.reflectionsByOthers?.filter((e) => e.note && e.note.length > 0) ||
    [];

  const completedSparks = sparks?.filter((spark) => spark.done).length;
  const sparkNotInGoals = sparks?.filter((spark) => spark.notInGoals).length;

  const completedAllSparks = !!(
    sparkNotInGoals ||
    0 ||
    (completedSparks || 0) >= sparks?.length
  );

  const isFirstAssessor = assessment?.first_assessor_id === userId;
  const isSecondAssessor = assessment?.second_assessor_id === userId;
  const isViewOnly =
    assessment?.first_assessor_done === 1 &&
    assessment.second_assessor_done === 1;

  return (
    <motion.div styleName="wrapper " {...framerFade}>
      <div styleName="reflection">
        <div styleName="spark-wrapper">
          <div
            onClick={() => setShowSparks((prev) => !prev)}
            styleName="spark-header"
          >
            <div styleName="spark-text ">
              <h3>Waar heeft de student aan gewerkt</h3>
              <span>
                Binnen deze vaardigheid heeft de student aan deze sparks
                gewerkt. De sparks met een vinkje zijn vooraf als doel gesteld.
              </span>
            </div>
            <ChevronArrow isOpen={showSparks} />
          </div>

          {showSparks && (
            <AnimatePresence>
              <motion.div {...framerFade} styleName="sparks">
                {sparks?.map((spark) => (
                  <>
                    <div
                      key={spark.id}
                      styleName={`spark ${
                        spark.done || spark.notInGoals ? '' : 'not-done'
                      }`}
                    >
                      <p>{spark.name}</p>
                    </div>
                    <ActivityItem spark={spark} />
                  </>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
        <div styleName="spark-wrapper">
          <div
            onClick={() => setShowEvidence((prev) => !prev)}
            styleName="spark-header"
          >
            <div styleName="spark-text">
              <h3>Gerelateerde bewijsvoering</h3>
              <span>
                Binnen deze vaardigheid heeft de student de volgende
                bewijsvoering toegevoegd:
              </span>
            </div>
            <ChevronArrow isOpen={showEvidence} />
          </div>
          {showEvidence && (
            <>
              {projectEvidence.length ? (
                <AnimatePresence>
                  <div styleName="evidence">
                    <EvidencePreview
                      type="assessment"
                      deleteCondition={!isLocked}
                      rootModal
                      evidence={projectEvidence}
                    />
                  </div>
                </AnimatePresence>
              ) : (
                <div styleName="no-select-wrapper">
                  <p styleName="no-select">
                    Geen bewijsvoering gerelateerd aan deze vaardigheid
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <div styleName="spark-wrapper">
          <div
            onClick={() => setShowFeedback((prev) => !prev)}
            styleName="spark-header"
          >
            <div styleName="spark-text">
              <h3>Ontvangen feedback</h3>
              <span>
                De student heeft de volgende feedback tijdens activiteiten in
                deze vaardigheid ontvangen:
              </span>
            </div>
            <ChevronArrow isOpen={showFeedback} />
          </div>
          {showFeedback && (
            <>
              {projectFeedback.length > 0 ? (
                <AnimatePresence>
                  <div styleName="feedback">
                    {projectFeedback.map((item) => {
                      const activityBySpark = getActivityBySpark(
                        { process_ids: [item.project_process_id] },
                        activities,
                        trainings,
                        assessment
                      );
                      return (
                        <ReviewItem
                          user={item.user}
                          item={item.note[0]?.answer || ''}
                          name={activityBySpark[0]?.name}
                          type={activityBySpark[0]?.type}
                        />
                      );
                    })}
                  </div>
                </AnimatePresence>
              ) : (
                <div styleName="no-select-wrapper">
                  <p styleName="no-select">
                    Geen feedback ontvangen in deze vaardigheid
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div styleName="peer-reflection">
        {completedAllSparks ? (
          <>
            <div>
              <h3>
                {isSecondAssessor
                  ? 'Oordeel eerste beoordelaar'
                  : isFirstAssessor
                  ? 'Jouw oordeel'
                  : 'Het oordeel'}
              </h3>
              <span>
                {isFirstAssessor
                  ? 'Beoordeel of deze student de vaardigheid goed beheerst. Als je van mening bent dat de student beroepsvaardig is in deze vaardigheid, vink dit dan aan en geef een korte toelichting. Deze informatie dient als onderbouwing voor de tweede beoordelaar.'
                  : 'Dit is het oordeel van de eerste beoordelaar over deze vaardigheid.'}
              </span>
            </div>
            <>
              {!isViewOnly && isFirstAssessor ? (
                <div styleName="textBoxes ">
                  {/* What can be improved? */}
                  <div>
                    <ReviewTextArea
                      setValue={setFirstImprovementValue}
                      item={firstReviewer}
                      value={firstImprovementValue}
                      type="improvement"
                      isExtern
                    />
                  </div>
                  {/* What went well? */}
                  <div>
                    <ReviewTextArea
                      setValue={setFirstPositiveValue}
                      item={firstReviewer}
                      value={firstPositiveValue}
                      type="good"
                      isExtern
                    />
                  </div>
                </div>
              ) : null}
              <div styleName="checkbox">
                <Checkbox
                  disabled={!isFirstAssessor}
                  isChecked={isChecked}
                  setIsChecked={setIsChecked}
                  type="green"
                >
                  <p>
                    Deze student heeft bewezen beroepsvaardig te zijn in deze
                    vaardigheid
                  </p>
                </Checkbox>
              </div>
              {isViewOnly || isSecondAssessor ? (
                <ReviewItem
                  user={assessment?.first_assessor}
                  good={firstReviewer}
                  improvement={firstImprovementReviewer}
                />
              ) : null}
            </>
            {isSecondAssessor && !isViewOnly ? (
              <>
                <div styleName="bar" />
                <ReviewTextArea
                  setValue={setSecondValue}
                  item={secondReviewer}
                  value={secondValue}
                  isExtern={false}
                />
              </>
            ) : null}
            {isViewOnly && !secondAssessorAgrees ? (
              <>
                <div styleName="bar" />
                <div styleName="reason">
                  <h3>
                    {skill?.pivot?.approved
                      ? 'Reden van goedkeuring'
                      : 'Reden van afwijzing'}
                  </h3>
                  <span>
                    {skill?.pivot?.approved
                      ? 'Ondanks het oordeel van de eerste beoordelaar heeft de tweede beoordelaar jou toch beroepsvaardig gevonden, met de volgende reden:'
                      : 'Ondanks het oordeel van de eerste beoordelaar heeft de tweede beoordelaar jou niet beroepsvaardig gevonden, met de volgende reden:'}
                  </span>
                  <ReviewItem
                    user={assessment?.second_assessor}
                    item={secondValue}
                  />
                </div>
              </>
            ) : null}
            {isViewOnly && secondAssessorAgrees ? (
              <ReviewItem user={assessment?.second_assessor} agrees />
            ) : null}
          </>
        ) : (
          <div styleName="sparks-not-completed">
            <h3>Je kunt deze vaardigheid nog niet beoordelen</h3>
            <p>
              Deze vaardigheid kan pas beoordeeld worden nadat de student alle
              sparks heeft afgevinkt, wat op dit moment niet het geval is.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
